"use client";

/**
 * @module ScrollArea
 *
 * Custom scrollable container with styled scrollbars and optional fade indicators.
 * Built on Radix UI ScrollArea primitive.
 *
 * @see {@link https://ui.shadcn.com/docs/components/scroll-area Shadcn ScrollArea}
 * @see {@link https://www.radix-ui.com/primitives/docs/components/scroll-area Radix ScrollArea}
 *
 * @example
 * // Basic scroll area
 * <ScrollArea className="h-72 w-48">
 *   <div className="p-4">
 *     {longContent}
 *   </div>
 * </ScrollArea>
 *
 * @example
 * // With fade indicators
 * <ScrollArea className="h-48" fadeAway="y">
 *   {content}
 * </ScrollArea>
 *
 * @example
 * // Hidden scrollbar
 * <ScrollArea className="h-48" hideScrollBar>
 *   {content}
 * </ScrollArea>
 *
 * @example
 * // Max-height constraint on the viewport
 * <ScrollArea fadeAway="bottom" viewportClassName="max-h-80">
 *   {content}
 * </ScrollArea>
 */
import { ScrollArea as ScrollAreaPrimitive } from "radix-ui";
import type { Ref } from "react";
import * as React from "react";
import { cn } from "../lib/utils";

// ponytail: minimal leading+trailing throttle with cancel — replaces a full
// lodash dependency that this file was the only consumer of.
interface ThrottledFunction<Args extends unknown[]> {
  cancel(): void;
  (...args: Args): void;
}
function throttle<Args extends unknown[]>(fn: (...args: Args) => void, wait: number): ThrottledFunction<Args> {
  let lastCall = 0;
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: Args | null = null;
  const invoke = (args: Args) => {
    lastCall = Date.now();
    fn(...args);
  };
  const throttled = (...args: Args) => {
    lastArgs = args;
    const remaining = wait - (Date.now() - lastCall);
    if (remaining <= 0) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      invoke(args);
    } else if (!timeout) {
      timeout = setTimeout(() => {
        timeout = null;
        if (lastArgs) {
          invoke(lastArgs);
        }
      }, remaining);
    }
  };
  throttled.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
    lastCall = 0;
    lastArgs = null;
  };
  return throttled;
}

/** Styled scrollbar component for vertical or horizontal scrolling. */
const ScrollBar = ({
  className,
  orientation = "vertical",
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar> & {
  ref?: Ref<React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>>;
}) => <ScrollAreaPrimitive.ScrollAreaScrollbar className={cn("flex touch-none select-none transition-colors", orientation === "vertical" && "h-full w-2.5 border-0 border-l border-l-transparent border-solid p-[1px]", orientation === "horizontal" && "h-2.5 flex-col border-0 border-t border-t-transparent border-solid p-[1px]", className)} orientation={orientation} ref={ref} {...props} data-sentry-element="ScrollAreaPrimitive.ScrollAreaScrollbar" data-sentry-component="ScrollBar" data-sentry-source-file="scroll-area.tsx">
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-gray-200 dark:bg-gray-800" data-sentry-element="ScrollAreaPrimitive.ScrollAreaThumb" data-sentry-source-file="scroll-area.tsx" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>;

/**
 * Props for FadeAway gradient overlay.
 * @property direction - Edge to show fade: `"top"`, `"bottom"`, `"right"`, or `"left"`
 * @property show - Whether the fade is visible
 */
interface FadeAwayProps extends React.HTMLAttributes<HTMLDivElement> {
  direction: "top" | "bottom" | "right" | "left";
  show: boolean;
}
const FadeAway = ({
  className,
  direction,
  show,
  ref,
  ...props
}: FadeAwayProps & {
  ref?: Ref<HTMLDivElement>;
}) => <div className={cn("pointer-events-none absolute z-8 transition-opacity duration-200", direction === "top" && "top-0 right-0 left-0 h-(--fade-away-top-height,32px) bg-linear-to-b", direction === "bottom" && "right-0 bottom-0 left-0 h-(--fade-away-bottom-height,32px) bg-linear-to-t", direction === "right" && "top-0 right-0 bottom-0 w-(--fade-away-right-width,32px) bg-linear-to-l", direction === "left" && "top-0 bottom-0 left-0 w-(--fade-away-left-width,32px) bg-linear-to-r", "from-(--fade-away-from,rgb(255_255_255)) to-(--fade-away-to,rgb(255_255_255/0)) dark:from-(--fade-away-from-dark,rgb(21_25_25)) dark:to-(--fade-away-to-dark,rgb(21_25_25/0))", show ? "opacity-100" : "opacity-0", className)} ref={ref} {...props} data-sentry-component="FadeAway" data-sentry-source-file="scroll-area.tsx" />;

/**
 * Props for ScrollArea component.
 * @property fadeAway - Show fade gradients: `true` (all edges), `"top"`, `"bottom"`, `"y"`, `"left"`, `"right"`, `"x"`
 * @property hideScrollBar - Hide the scrollbar while keeping functionality
 * @property viewportClassName - Classes applied to the scrollable viewport. The viewport is `h-full`, which cannot resolve against a `max-h`-only root — put `max-h-*` constraints here instead
 */
interface ScrollAreaProps extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  fadeAway?: boolean | "top" | "bottom" | "y" | "left" | "right" | "x";
  hideScrollBar?: boolean;
  viewportClassName?: string;
}
interface ScrollFadeParams {
  enabled: boolean;
  viewportRef: React.RefObject<HTMLDivElement | null>;
}
const useScrollFade = ({
  viewportRef,
  enabled
}: ScrollFadeParams) => {
  const [showTopFade, setShowTopFade] = React.useState(false);
  const [showBottomFade, setShowBottomFade] = React.useState(true);
  const [showLeftFade, setShowLeftFade] = React.useState(false);
  const [showRightFade, setShowRightFade] = React.useState(true);
  const handleScroll = React.useCallback(() => {
    if (!enabled) {
      return;
    }
    if (viewportRef.current) {
      const {
        scrollTop,
        scrollLeft,
        scrollHeight,
        scrollWidth,
        clientHeight,
        clientWidth
      } = viewportRef.current;
      setShowTopFade(scrollTop > 0);
      setShowBottomFade(scrollTop < scrollHeight - clientHeight - 1);
      setShowLeftFade(scrollLeft > 0);
      setShowRightFade(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, [viewportRef, enabled]);
  const throttledHandleScroll = React.useMemo(() => throttle(handleScroll, 50), [handleScroll]);
  React.useEffect(() => {
    if (!enabled) {
      return;
    }
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    // Initial check
    throttledHandleScroll();

    // Handle scroll events
    viewport.addEventListener("scroll", throttledHandleScroll, {
      passive: true
    });

    // Handle resize events that might affect scrollability
    const resizeObserver = new ResizeObserver(throttledHandleScroll);
    resizeObserver.observe(viewport);
    return () => {
      viewport.removeEventListener("scroll", throttledHandleScroll);
      resizeObserver.disconnect();
      throttledHandleScroll.cancel(); // Clean up throttled function
    };
  }, [throttledHandleScroll, viewportRef, enabled]);
  return {
    showTopFade,
    showBottomFade,
    showLeftFade,
    showRightFade
  };
};
const ScrollArea = ({
  className,
  children,
  fadeAway = false,
  hideScrollBar,
  viewportClassName,
  ref,
  ...props
}: ScrollAreaProps & {
  ref?: Ref<React.ElementRef<typeof ScrollAreaPrimitive.Root>>;
}) => {
  const internalViewportRef = React.useRef<HTMLDivElement>(null);
  const {
    showTopFade,
    showBottomFade,
    showLeftFade,
    showRightFade
  } = useScrollFade({
    viewportRef: internalViewportRef,
    enabled: !!fadeAway
  });
  const shouldHideScrollBar = "hideScrollBar" in props || hideScrollBar === true;
  return <ScrollAreaPrimitive.Root className={cn("relative overflow-hidden", className)} ref={ref} {...props} data-sentry-element="ScrollAreaPrimitive.Root" data-sentry-component="ScrollArea" data-sentry-source-file="scroll-area.tsx">
      <ScrollAreaPrimitive.Viewport className={cn("h-full w-full rounded-[inherit]", viewportClassName)} ref={internalViewportRef} data-sentry-element="ScrollAreaPrimitive.Viewport" data-sentry-source-file="scroll-area.tsx">
        {fadeAway && (fadeAway === true || fadeAway === "top" || fadeAway === "y") && <FadeAway direction="top" show={showTopFade} />}
        {fadeAway && (fadeAway === true || fadeAway === "left" || fadeAway === "x") && <FadeAway direction="left" show={showLeftFade} />}
        {children}
        {fadeAway && (fadeAway === true || fadeAway === "bottom" || fadeAway === "y") && <FadeAway direction="bottom" show={showBottomFade} />}
        {fadeAway && (fadeAway === true || fadeAway === "right" || fadeAway === "x") && <FadeAway direction="right" show={showRightFade} />}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar className={shouldHideScrollBar ? "hidden opacity-0" : ""} data-sentry-element="ScrollBar" data-sentry-source-file="scroll-area.tsx" />
      <ScrollAreaPrimitive.Corner data-sentry-element="ScrollAreaPrimitive.Corner" data-sentry-source-file="scroll-area.tsx" />
    </ScrollAreaPrimitive.Root>;
};
export { FadeAway, ScrollArea, type ScrollAreaProps, ScrollBar };